import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";

/**
 * VIB-1207: «Tildel alle timer i fase» — bulk-tildel alle interne oppgavelinjer
 * i én fase(-gruppe) til én person med ett klikk.
 *
 * Egen hook-fil (ikke utvidelse av den 372-linjers `use-budget-assignments.ts`)
 * per filstørrelses-regelen. Gjenbruker upsert-mønsteret derfra, men trenger
 * kun INSERT: strategien er «fyll tomt» (default og eneste i v1), så vi rører
 * ALDRI eksisterende rader — ingen UPDATE, ingen DELETE. Det verner
 * `task_status`/`completed_at`/`approved_*`-historikk automatisk (grunnlaget
 * for faktureringsgrad), og audit dekkes av DB-triggeren `log_assignment_change`
 * (INSERT-gren) — ingen frontend-logEvent nødvendig.
 */

export interface ExistingAssignment {
  user_id: string;
  hours: number | null;
}

export interface BulkAssignLine {
  id: string;
  /** Budsjetterte timer på linjen (`quantity_hours`). */
  quantityHours: number;
}

export interface BulkAssignPlan {
  inserts: Array<{ budget_line_id: string; hours: number }>;
  /** Antall linjer hoppet over (personen har allerede rad, eller 0 rest). */
  skipped: number;
}

/**
 * Ren planleggingslogikk (testbar uten Supabase). Strategi «fyll tomt»:
 * - Har personen allerede en rad på linjen → hopp over (bevar eksisterende).
 * - Ellers: tildel resten = `quantityHours − Σ andres tildelte timer` (min 0).
 * - 0 (eller negativ) rest → hopp over (ingenting meningsfullt å tildele).
 */
export function planBulkPhaseAssignment(
  lines: BulkAssignLine[],
  existingByLine: Map<string, ExistingAssignment[]>,
  userId: string,
): BulkAssignPlan {
  const inserts: BulkAssignPlan["inserts"] = [];
  let skipped = 0;

  for (const line of lines) {
    const existing = existingByLine.get(line.id) ?? [];
    if (existing.some((a) => a.user_id === userId)) {
      skipped++;
      continue;
    }
    const othersHours = existing.reduce((s, a) => s + (a.hours ?? 0), 0);
    const rest = Math.max(0, (line.quantityHours ?? 0) - othersHours);
    if (rest <= 0) {
      skipped++;
      continue;
    }
    inserts.push({ budget_line_id: line.id, hours: rest });
  }

  return { inserts, skipped };
}

interface BulkAssignPhaseParams {
  projectId: string;
  userId: string;
  /**
   * Interne oppgavelinjer i fasen (id + budsjetterte timer). Kallstedet
   * filtrerer allerede til task-linjer (`isTaskLine`) og interne linjer.
   */
  lines: BulkAssignLine[];
}

export function useBulkAssignPhase() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ userId, lines }: BulkAssignPhaseParams): Promise<BulkAssignPlan> => {
      if (lines.length === 0 || !userId) {
        return { inserts: [], skipped: 0 };
      }

      const ids = lines.map((l) => l.id);
      const { data: existing, error: selErr } = await supabase
        .from("budget_line_assignments")
        .select("budget_line_id, user_id, hours")
        .in("budget_line_id", ids);
      if (selErr) throw new Error(`Lesing feilet: ${selErr.message}`);

      const existingByLine = new Map<string, ExistingAssignment[]>();
      for (const row of existing ?? []) {
        const r = row as { budget_line_id: string; user_id: string; hours: number | null };
        const arr = existingByLine.get(r.budget_line_id) ?? [];
        arr.push({ user_id: r.user_id, hours: r.hours });
        existingByLine.set(r.budget_line_id, arr);
      }

      const plan = planBulkPhaseAssignment(lines, existingByLine, userId);

      if (plan.inserts.length > 0) {
        const newRows = plan.inserts.map((i) => ({
          budget_line_id: i.budget_line_id,
          user_id: userId,
          hours: i.hours,
          // VIB-1169: manuell tildeling låses så Monday-import ikke overskriver.
          hours_locked: true,
        }));
        const { error: insErr } = await supabase
          .from("budget_line_assignments")
          .insert(newRows);
        if (insErr) throw new Error(`Tildeling feilet: ${insErr.message}`);
      }

      return plan;
    },
    onSuccess: async (plan, variables) => {
      await invalidateFor(qc, "budget_line_assignments", { projectId: variables.projectId });
      const assigned = plan.inserts.length;
      if (assigned === 0) {
        toast.info("Ingen linjer å tildele (alle allerede tildelt eller fylt opp)");
      } else if (plan.skipped > 0) {
        toast.success(`${assigned} oppgaver tildelt · ${plan.skipped} hoppet over`);
      } else {
        toast.success(`${assigned} oppgaver tildelt`);
      }
      // VIB-1184 D15: assignment_created (med snapshot) logges autoritativt av
      // DB-triggeren log_assignment_change. Ingen frontend-dupp.
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error(`Bulk-tildeling feilet: ${(err as Error).message}`);
    },
  });
}
